import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
    Trash2,
    XCircle,
    RefreshCw,
    Loader2,
    Search,
    User,
    Clock,
    AlertTriangle,
    CheckCircle,
    Database,
    Filter
} from 'lucide-react';

const SourcingSessionManager = () => {
    const { api } = useAuth();
    const [loading, setLoading] = useState(true);
    const [sessions, setSessions] = useState([]);
    const [stats, setStats] = useState({ total: 0, active: 0, completed: 0, abandoned: 0 });
    const [userEmailFilter, setUserEmailFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [actionLoading, setActionLoading] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    useEffect(() => {
        fetchSessions();
    }, []);

    const fetchSessions = async (email = '', status = '') => {
        setLoading(true);
        try {
            let url = '/sourcing-labs/admin/sessions?limit=100';
            if (email) url += `&user_email=${encodeURIComponent(email)}`;
            if (status) url += `&status=${status}`;
            
            const response = await api.get(url);
            setSessions(response.data.sessions || []);
            setStats(response.data.stats || { total: 0, active: 0, completed: 0, abandoned: 0 });
        } catch (error) {
            console.error('Failed to fetch sessions:', error);
            toast.error('Failed to load sessions');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        fetchSessions(userEmailFilter, statusFilter);
    };

    const confirmAction = (action, session) => {
        setPendingAction({ action, session });
        setShowConfirmDialog(true);
    };

    const executeAction = async () => {
        if (!pendingAction) return;
        
        const { action, session } = pendingAction;
        setActionLoading(session.id);
        setShowConfirmDialog(false);
        
        try {
            if (action === 'delete') {
                await api.delete(`/sourcing-labs/admin/sessions/${session.id}`);
                toast.success('Session deleted', {
                    description: `Deleted session for ${session.user_email}`
                });
            } else if (action === 'abandon') {
                await api.post(`/sourcing-labs/admin/sessions/${session.id}/abandon`);
                toast.success('Session abandoned', {
                    description: `Marked session as abandoned for ${session.user_email}`
                });
            } else if (action === 'clear-all') {
                await api.delete(`/sourcing-labs/admin/sessions/user/${encodeURIComponent(session.user_email)}/clear-active`);
                toast.success('Active sessions cleared', {
                    description: `All active sessions cleared for ${session.user_email}`
                });
            }
            fetchSessions(userEmailFilter, statusFilter);
        } catch (error) {
            toast.error(`Failed to ${action} session`, {
                description: error.response?.data?.detail || 'Unknown error'
            });
        } finally {
            setActionLoading(null);
            setPendingAction(null);
        }
    };

    const getStatusBadge = (status) => {
        const config = {
            'job_briefing': { label: 'Job Briefing', className: 'bg-blue-500/20 text-blue-700' },
            'search_strategy': { label: 'Search Strategy', className: 'bg-amber-500/20 text-amber-700' },
            'candidate_review': { label: 'Candidate Review', className: 'bg-purple-500/20 text-purple-700' },
            'ai_discussion': { label: 'AI Discussion', className: 'bg-cyan-500/20 text-cyan-700' },
            'completed': { label: 'Completed', className: 'bg-emerald-500/20 text-emerald-700' },
            'abandoned': { label: 'Abandoned', className: 'bg-gray-500/20 text-gray-700' }
        };
        const { label, className } = config[status] || { label: status, className: 'bg-gray-500/20' };
        return <Badge className={className}>{label}</Badge>;
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-primary" />
                    Sourcing Lab Session Manager
                </CardTitle>
                <CardDescription>
                    Manage and clear corrupted or stuck sourcing lab sessions
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-4 gap-4">
                    <div className="p-3 rounded-lg bg-muted text-center">
                        <p className="text-2xl font-bold">{stats.total}</p>
                        <p className="text-xs text-muted-foreground">Total</p>
                    </div>
                    <div className="p-3 rounded-lg bg-amber-500/10 text-center">
                        <p className="text-2xl font-bold text-amber-600">{stats.active}</p>
                        <p className="text-xs text-muted-foreground">Active</p>
                    </div>
                    <div className="p-3 rounded-lg bg-emerald-500/10 text-center">
                        <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
                        <p className="text-xs text-muted-foreground">Completed</p>
                    </div>
                    <div className="p-3 rounded-lg bg-gray-500/10 text-center">
                        <p className="text-2xl font-bold text-gray-600">{stats.abandoned}</p>
                        <p className="text-xs text-muted-foreground">Abandoned</p>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex items-end gap-3">
                    <div className="flex-1">
                        <Label htmlFor="email-filter">User Email</Label>
                        <Input
                            id="email-filter"
                            placeholder="Filter by user email..."
                            value={userEmailFilter}
                            onChange={(e) => setUserEmailFilter(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                    </div>
                    <div className="w-48">
                        <Label htmlFor="status-filter">Status</Label>
                        <select
                            id="status-filter"
                            className="w-full h-10 px-3 rounded-md border bg-background"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">All Statuses</option>
                            <option value="job_briefing">Job Briefing</option>
                            <option value="search_strategy">Search Strategy</option>
                            <option value="candidate_review">Candidate Review</option>
                            <option value="ai_discussion">AI Discussion</option>
                            <option value="completed">Completed</option>
                            <option value="abandoned">Abandoned</option>
                        </select>
                    </div>
                    <Button onClick={handleSearch} disabled={loading}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" onClick={() => fetchSessions()} disabled={loading}>
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>

                {/* Sessions List */}
                <ScrollArea className="h-[400px] border rounded-lg">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : sessions.length === 0 ? (
                        <div className="text-center py-12">
                            <CheckCircle className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
                            <h3 className="font-medium">No Sessions Found</h3>
                            <p className="text-muted-foreground">No matching sessions found</p>
                        </div>
                    ) : (
                        <div className="divide-y">
                            {sessions.map((session) => (
                                <div 
                                    key={session.id}
                                    className="flex items-center justify-between p-4 hover:bg-muted/50"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`p-2 rounded-full ${
                                            session.status === 'completed' ? 'bg-emerald-500/10' :
                                            session.status === 'abandoned' ? 'bg-gray-500/10' :
                                            'bg-amber-500/10'
                                        }`}>
                                            {session.status === 'completed' ? (
                                                <CheckCircle className="h-4 w-4 text-emerald-500" />
                                            ) : session.status === 'abandoned' ? (
                                                <XCircle className="h-4 w-4 text-gray-500" />
                                            ) : (
                                                <Clock className="h-4 w-4 text-amber-500" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-sm">{session.lab_name || 'Unknown Lab'}</p>
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                <User className="h-3 w-3" />
                                                {session.user_email || session.user_name || 'Unknown'}
                                                <span>•</span>
                                                {session.created_at && formatDistanceToNow(new Date(session.created_at), { addSuffix: true })}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {getStatusBadge(session.status)}
                                        {session.status !== 'completed' && session.status !== 'abandoned' && (
                                            <>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => confirmAction('abandon', session)}
                                                    disabled={actionLoading === session.id}
                                                >
                                                    {actionLoading === session.id ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <XCircle className="h-4 w-4" />
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="destructive"
                                                    size="sm"
                                                    onClick={() => confirmAction('delete', session)}
                                                    disabled={actionLoading === session.id}
                                                >
                                                    {actionLoading === session.id ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <Trash2 className="h-4 w-4" />
                                                    )}
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </ScrollArea>

                {/* Quick Actions */}
                {userEmailFilter && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-red-500" />
                            <span className="text-sm font-medium">Clear all active sessions for {userEmailFilter}?</span>
                        </div>
                        <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => confirmAction('clear-all', { user_email: userEmailFilter })}
                            disabled={actionLoading}
                        >
                            Clear All Active
                        </Button>
                    </div>
                )}
            </CardContent>

            {/* Confirmation Dialog */}
            <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                            Confirm Action
                        </DialogTitle>
                        <DialogDescription>
                            {pendingAction?.action === 'delete' && (
                                <>Are you sure you want to <strong>permanently delete</strong> this session? This cannot be undone.</>
                            )}
                            {pendingAction?.action === 'abandon' && (
                                <>Mark this session as <strong>abandoned</strong>? The user will be able to start a new session.</>
                            )}
                            {pendingAction?.action === 'clear-all' && (
                                <>Clear <strong>all active sessions</strong> for {pendingAction?.session?.user_email}? They will be able to start fresh.</>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                            Cancel
                        </Button>
                        <Button 
                            variant={pendingAction?.action === 'delete' || pendingAction?.action === 'clear-all' ? 'destructive' : 'default'}
                            onClick={executeAction}
                        >
                            {pendingAction?.action === 'delete' ? 'Delete Session' :
                             pendingAction?.action === 'abandon' ? 'Mark Abandoned' :
                             'Clear All Active'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
};

export default SourcingSessionManager;
